import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputMask } from "primereact/inputmask";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  CARE_PLAN_COMMUNICATION_PHONE_OPTIONS,
  CarePlanSectionProps,
  NA,
  NO,
  RESIDENT_CARE_PLAN_TYPE,
  RESIDENT_CARE_PLAN_TYPE_MESSAGES,
  RESIDENT_HAS_OWN_PHONE,
  YES
} from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE, RESIDENT_STRENGTH_MESSAGE } from "../../../constants/carePlanConstants";
import CommunicationGenieDialog from "./CommunicationGenieDialog";
import { CommunicationGenieSectionValues } from "./communicationGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const CommunicationSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.COMMUNICATION,
    problemWithModesOfExpression: sectionData?.problemWithModesOfExpression || false,
    expressionDescribed: sectionData?.expressionDescribed || '',
    expressionEquipmentUsed: sectionData?.expressionEquipmentUsed || '',

    problemWithHearing: sectionData?.problemWithHearing || false,
    hearingDescribed: sectionData?.hearingDescribed || '',
    hearingEquipmentUsed: sectionData?.hearingEquipmentUsed || '',

    problemWithVision: sectionData?.problemWithVision || false,
    visionDescribed: sectionData?.visionDescribed || '',
    visionEquipmentUsed: sectionData?.visionEquipmentUsed || '',

    abilityToUsePhone: sectionData?.abilityToUsePhone || false,
    residentPhoneNumber: sectionData?.residentPhoneNumber || '',

    howResidentMakesSelfUnderstood: sectionData?.howResidentMakesSelfUnderstood || '',
    howResidentUnderstandOthers: sectionData?.howResidentUnderstandOthers || '',

    anyStrengthAndAbilities: sectionData?.anyStrengthAndAbilities || '',
    anyAssistanceRequired: sectionData?.anyAssistanceRequired || '',

    anyComments: sectionData?.anyComments || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, getValues, setValue } = useForm({ defaultValues });
  const watchProblemWithModesOfExpression = watch("problemWithModesOfExpression", defaultValues.problemWithModesOfExpression);
  const watchProblemWithHearing = watch("problemWithHearing", defaultValues.problemWithHearing);
  const watchProblemWithVision = watch("problemWithVision", defaultValues.problemWithVision);
  const watchAbilityToUsePhone = watch("abilityToUsePhone", defaultValues.abilityToUsePhone);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <CommunicationGenieDialog
        visible={showGenieDialog}
        currentValues={getValues() as CommunicationGenieSectionValues}
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = {
            ...getValues(),
            ...payload.valueUpdates,
          };

          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });

          await onsubmit(nextValues);
        }}
      />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Mode Of expression</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.problemWithModesOfExpression })}>Any problems with modes of expression:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="problemWithModesOfExpression"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('problemWithModesOfExpression')}
                    </div>
                    {
                      watchProblemWithModesOfExpression && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="expressionDescribed" control={control}
                                rules={{ required: 'Please explain' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="expressionDescribed" className={classNames({ 'p-error': errors.expressionDescribed })}>Expression described</label>
                            </span>
                            {getFormErrorMessage('expressionDescribed')}
                          </div>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="expressionEquipmentUsed" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="expressionEquipmentUsed" className={classNames({ 'p-error': errors.expressionEquipmentUsed })}>Expression equipment used</label>
                            </span>
                            {getFormErrorMessage('expressionEquipmentUsed')}
                          </div>
                        </>)
                    }
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any problems with modes of expression: </strong> {sectionData?.problemWithModesOfExpression ? YES : NO}</div>
                    {sectionData?.problemWithModesOfExpression && (
                      <>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Expression described: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.expressionDescribed || NA}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Expression equipment used: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.expressionEquipmentUsed || NA}</span>
                        </div>
                      </>
                    )}
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Hearing</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.problemWithHearing })}>Any problems with hearing:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="problemWithHearing"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('problemWithHearing')}
                    </div>
                    {
                      watchProblemWithHearing && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="hearingDescribed" control={control}
                                rules={{ required: 'Please explain' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="hearingDescribed" className={classNames({ 'p-error': errors.hearingDescribed })}>Hearing described</label>
                            </span>
                            {getFormErrorMessage('hearingDescribed')}
                          </div>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="hearingEquipmentUsed" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="hearingEquipmentUsed" className={classNames({ 'p-error': errors.hearingEquipmentUsed })}>Hearing equipment used</label>
                            </span>
                            {getFormErrorMessage('hearingEquipmentUsed')}
                          </div>
                        </>)
                    }
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any problems with hearing: </strong> {sectionData?.problemWithHearing ? YES : NO}</div>
                    {sectionData?.problemWithHearing && (
                      <>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Hearing described: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.hearingDescribed || NA}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Hearing equipment used: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.hearingEquipmentUsed || NA}</span>
                        </div>
                      </>
                    )}
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Vision</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.problemWithVision })}>Any problems with vision:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="problemWithVision"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('problemWithVision')}
                    </div>
                    {
                      watchProblemWithVision && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="visionDescribed" control={control}
                                rules={{ required: 'Please explain' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="visionDescribed" className={classNames({ 'p-error': errors.visionDescribed })}>Vision described</label>
                            </span>
                            {getFormErrorMessage('visionDescribed')}
                          </div>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="visionEquipmentUsed" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="visionEquipmentUsed" className={classNames({ 'p-error': errors.visionEquipmentUsed })}>Vision equipment used</label>
                            </span>
                            {getFormErrorMessage('visionEquipmentUsed')}
                          </div>
                        </>)
                    }
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any problems with vision: </strong> {sectionData?.problemWithVision ? YES : NO}</div>
                    {sectionData?.problemWithVision && (
                      <>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Vision described: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.visionDescribed || NA}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Vision equipment used: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.visionEquipmentUsed || NA}</span>
                        </div>
                      </>
                    )}
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Phone</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.abilityToUsePhone })}>Ability to use the phone:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="abilityToUsePhone"
                          control={control}
                          render={({ field }) => (
                            <Dropdown placeholder="Select one ..." options={CARE_PLAN_COMMUNICATION_PHONE_OPTIONS}
                              {...field}
                              value={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('abilityToUsePhone')}
                    </div>
                    {
                      watchAbilityToUsePhone === RESIDENT_HAS_OWN_PHONE && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="residentPhoneNumber" control={control}
                                rules={{ required: 'Please explain' }}
                                render={({ field, fieldState }) => (
                                  <InputMask style={{ width: '100%' }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} mask="(999) 999-9999" />
                                )} />
                              <label htmlFor="residentPhoneNumber" className={classNames({ 'p-error': errors.residentPhoneNumber })}>Resident phone number</label>
                            </span>
                            {getFormErrorMessage('residentPhoneNumber')}
                          </div>
                        </>)
                    }
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Ability to use phone: </strong> {sectionData?.abilityToUsePhone || NA}</div>
                    {sectionData?.residentPhoneNumber && (
                      <>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident phone number: </strong> {sectionData?.residentPhoneNumber || NA}</div>
                      </>
                    )}
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Strength & assistance</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.howResidentMakesSelfUnderstood })}>How resident makes self-understood:</label>
                      <span className="p-float-label">
                        <Controller
                          name="howResidentMakesSelfUnderstood"
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )}
                        />
                      </span>
                      {getFormErrorMessage('howResidentMakesSelfUnderstood')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.howResidentUnderstandOthers })}>How resident understand others:</label>
                      <span className="p-float-label">
                        <Controller
                          name="howResidentUnderstandOthers"
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )}
                        />
                      </span>
                      {getFormErrorMessage('howResidentUnderstandOthers')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="anyStrengthAndAbilities" className={classNames({ 'p-error': errors.anyStrengthAndAbilities })}>
                          {RESIDENT_STRENGTH_MESSAGE}
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.COMMUNICATION_STRENGTH_ABILITIES) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller
                          name="anyStrengthAndAbilities"
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )}
                        />
                      </span>
                      {getFormErrorMessage('anyStrengthAndAbilities')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="anyAssistanceRequired" className={classNames({ 'p-error': errors.anyAssistanceRequired })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.COMMUNICATION_ASSISTANCE_REQUIRED) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller
                          name="anyAssistanceRequired"
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })}
                            />
                          )}
                        />
                      </span>
                      {getFormErrorMessage('anyAssistanceRequired')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyComments })}>Any overall comments:</label>
                      <span className="p-float-label">
                        <Controller
                          name="anyComments"
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )}
                        />
                      </span>
                      {getFormErrorMessage('anyComments')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>How resident makes self understood: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.howResidentMakesSelfUnderstood || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>How resident understand others: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.howResidentUnderstandOthers || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>{RESIDENT_STRENGTH_MESSAGE}: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.anyStrengthAndAbilities || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>{ASSISTANCE_REQUIRED_MESSAGE}: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.anyAssistanceRequired || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Any overall comments: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.anyComments || NA}</span>
                    </div>
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default CommunicationSection;


